import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { Dropdown } from '../ui';
import { useLanguage } from '../../contexts/LanguageContext';
import { SupportedLanguage } from '../../i18n';

/**
 * LanguageSelector Component
 *
 * Displays a dropdown to switch between available system languages
 * Positioned outside the body content on grey background, aligned left
 *
 * Uses local state to prevent race condition snap-back when changing language
 * Uses custom Dropdown component to match duration dropdown styling
 *
 * Supported Languages:
 * - English (en)
 * - Japanese (ja)
 */

interface LanguageSelectorProps {
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguage();

  // Use local state to prevent visual snap-back during async language change
  const [selectedLang, setSelectedLang] = useState(i18n.language);

  // Track user interaction to prevent syncing immediately after user clicks
  const userInteractedRef = useRef(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ja', name: '日本語' }
  ];

  // Sync local state with i18n language changes from other sources
  // BUT not immediately after user interaction to prevent being overridden
  useEffect(() => {
    // Don't sync if user just interacted with the selector
    if (userInteractedRef.current) {
      userInteractedRef.current = false;
      return;
    }

    const currentLang = i18n.language;
    if (currentLang !== selectedLang) {
      setSelectedLang(currentLang);
    }
  }, [i18n.language, selectedLang]);

  const handleLanguageChange = (value: string | number) => {
    const languageCode = value as string;

    // Mark user interaction to prevent immediate sync
    userInteractedRef.current = true;

    // Immediately update local state for instant UI feedback
    setSelectedLang(languageCode);

    // Set flag to indicate user has actively changed language this session
    sessionStorage.setItem('userChangedLanguage', 'true');

    // Change language AND persist it to the user's profile.
    // This used to call i18n.changeLanguage directly, which only changed the
    // UI — the account kept preferredLanguage unchanged, so a user who
    // switched to Japanese here still received every email in English.
    // changeLanguage() from LanguageContext updates both.
    changeLanguage(languageCode as SupportedLanguage).then(() => {
      localStorage.setItem('i18nextLng', languageCode);
    }).catch(() => {
      // Revert local state on error
      setSelectedLang(i18n.language);
      userInteractedRef.current = false;
    });
  };

  return (
    <div className={`flex items-center justify-start ${className}`}>
      <div className="flex items-center space-x-2">
        <GlobeAltIcon className="h-4 w-4 text-secondary-500" />
        <div className="language-selector-dropdown">
          <Dropdown
            value={selectedLang}
            onChange={handleLanguageChange}
            options={languages.map(lang => ({
              value: lang.code,
              label: lang.name
            }))}
          />
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;
